import { addDays, format, differenceInCalendarDays, subDays } from 'date-fns';

import type {
  postDestinationsRequest,
  postDestinationsResponse,
} from '@lt/api/dist/methods/widgets/types';
import type { DateYMDString } from '@lt/api/dist/types/date';
import type { DepartureOption } from '@lt/api/dist/types/departure';
import type { DirectionWidgetItemProps } from '../components';
import type { DirectionWidgetData } from '../types';
import type { Values } from '../../../components/Cigarette/context';

export const SERVER_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * данные из конфиг стора конвертирует в параметры для поиска направлений
 * */
export const configSearchToDestination = (
  searchParams: DirectionWidgetData['searchParams'],
  initial: boolean
): postDestinationsRequest => {
  return {
    adults: searchParams.adults,
    currency: 'rub',
    departure_ids: searchParams.departure ? [searchParams.departure] : [],
    destination_ids: searchParams.destination_ids || [],
    end_date_from: format(
      addDays(new Date(searchParams.start_date as DateYMDString), searchParams.nights.min),
      SERVER_DATE_FORMAT
    ) as DateYMDString,
    end_date_till: format(
      addDays(new Date(searchParams.start_date as DateYMDString), searchParams.nights.max),
      SERVER_DATE_FORMAT
    ) as DateYMDString,
    flex_dates: 0,
    initial,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    kids: searchParams.kids,
    locale: 'ru',
    nights: {
      from: searchParams.nights.min,
      to: searchParams.nights.max,
    },
    search_type: 'package',
    start_date: searchParams.start_date as DateYMDString,
    start_date_from: searchParams.start_date as DateYMDString,
    start_date_till: searchParams.start_date as DateYMDString,
  };
};

export const mapDestinationsOffersToDirectionItem = (
  offers: postDestinationsResponse['offers'],
  tourists: { kids: Array<number>; adults: number }
): DirectionWidgetItemProps[] => {
  return offers.map((offer): DirectionWidgetItemProps => {
    return {
      adults: tourists.adults,
      description: offer?.destination.description,
      image: offer?.destination.images['x543x285'],
      link: offer?.search_link,
      destinationName: offer?.destination.name,
      departureName: offer.departure.name,
      kids: tourists.kids,
      nights: offer?.nights,
      price: offer?.price.amount,
      siblings_departures: [],
      start_date: offer?.start_date,
      visa: {
        visa_text: offer?.destination?.visa?.name,
        visa_type: offer?.destination?.visa?.type,
      },
    };
  });
};

export type SearchParams = SearchParamsWithNights | SearchParamsWithoutNights;

type CommonSearchParams = {
  adults: number;
  kids?: number[];
  departure?: DepartureOption;
};

export type SearchParamsWithNights = CommonSearchParams & {
  start_date: Date;
  nights: {
    min: number;
    max: number;
  };
};

type SearchParamsWithoutNights = CommonSearchParams & {
  start_date_from?: Date;
  start_date_till?: Date;
  end_date_from?: Date;
  end_date_till?: Date;
};

export const cigaretteValuesToSearchParams = (
  values: Values,
  withoutNights = false
): SearchParams | null => {
  if (!values.datepicker || !values.datepicker.left || !values.datepicker.right) {
    return null;
  }

  const startDateCell = values.datepicker.left;
  const endDateCell = values.datepicker.right;
  if (!startDateCell.date || !endDateCell.date) {
    return null;
  }

  const middleNights = differenceInCalendarDays(endDateCell.date, startDateCell.date);
  const nightsInterval =
    middleNights > 1 ? [middleNights - 1, middleNights + 1] : [middleNights, middleNights + 2];

  const searchParams = {
    adults: values.tourists.adults,
    ...(values.tourists.kids?.length && { kids: values.tourists.kids }),
    departure: values.departure,
    ...(withoutNights && {
      start_date_from: subDays(startDateCell.date, values.datepicker.shift),
      start_date_till: addDays(startDateCell.date, values.datepicker.shift),
      end_date_from: subDays(endDateCell.date, values.datepicker.shift),
      end_date_till: addDays(endDateCell.date, values.datepicker.shift),
    }),
    ...(!withoutNights && {
      start_date: startDateCell.date,
      nights: {
        min: nightsInterval[0],
        max: nightsInterval[1],
      },
    }),
    ...(values.destination && { destination_ids: [values.destination] }),
  };

  return searchParams;
};
